import { useState, useRef, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

// React-calendar value can be a single date, range of dates, or null
type CalendarValue = Date | [Date, Date] | [Date | null, Date | null] | null;

import { InputBox } from "../InputBox/InputBox";
import { Input } from "../Input/Input";
import { Icon } from "../Icon/Icon";

import classes from "./DateRangePicker.module.css";

type DateRange = [Date | null, Date | null];

type Props = {
  value?: DateRange;
  onChange: (range: DateRange) => void;
  placeholder?: string;
  disabled?: boolean;
  formatDate?: (date: Date) => string;
};

const defaultFormatDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const DateRangePicker = ({
  value,
  onChange,
  placeholder = "Select date range",
  disabled = false,
  formatDate = defaultFormatDate,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState<DateRange>(value || [null, null]);
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempRange(value || [null, null]);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setTempRange(value || [null, null]);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, value]);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleCalendarChange = (selectedValue: CalendarValue) => {
    if (selectedValue === null) {
      const emptyRange: DateRange = [null, null];
      setTempRange(emptyRange);
      onChange(emptyRange);
      return;
    }
    
    if (Array.isArray(selectedValue)) {
      const newRange: DateRange = [selectedValue[0] || null, selectedValue[1] || null];
      setTempRange(newRange);
      onChange(newRange);
      
      if (newRange[0] && newRange[1]) {
        setIsOpen(false);
      }
    } else if (selectedValue) {
      const newRange: DateRange = [selectedValue, null];
      setTempRange(newRange);
      onChange(newRange);
    }
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const emptyRange: DateRange = [null, null];
    setTempRange(emptyRange);
    onChange(emptyRange);
  };

  const getDisplayValue = (): string => {
    const [startDate, endDate] = value || [null, null];
    
    if (!startDate && !endDate) {
      return "";
    }
    
    if (startDate && !endDate) {
      return formatDate(startDate);
    }
    
    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    
    return "";
  };

  const displayValue = getDisplayValue();

  return (
    <div ref={containerRef} className={classes.container}>
      <InputBox
        wrap={false}
        rightSlot={
          <div className={classes.iconContainer}>
            {displayValue && !disabled && (
              <button
                type="button"
                onClick={handleClearClick}
                className={classes.clearButton}
                aria-label="Clear date range"
              >
                ×
              </button>
            )}
            <Icon icon="faCalendar" />
          </div>
        }
        rightOnClick={handleInputClick}
      >
        <Input
          value={displayValue}
          placeholder={placeholder}
          readOnly
          onClick={handleInputClick}
          disabled={disabled}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
      </InputBox>
      
      {isOpen && (
        <div ref={calendarRef} className={classes.calendarContainer}>
          <Calendar
            onChange={handleCalendarChange}
            value={tempRange}
            selectRange={true}
            maxDate={new Date()}
            className={classes.calendar}
          />
        </div>
      )}
    </div>
  );
};